<ion-header>
  <div class="upgrade-feature-banner" *ngIf="!featureEnabled && authService.isTenantAdmin">
    <div class="flex-space-between" style="align-items: center">
      Upgrade to run automations
      <ion-button
        fill="outline"
        color="light"
        class="btn-small"
        [routerLink]="'/upgrade'"
        [queryParams]="{ plan: 'startup' }"
      >
        <ion-icon name="arrow-up-circle" class="padded-icon"></ion-icon>
        Upgrade account
      </ion-button>
    </div>
  </div>

  <div class="constrained-content">
    <div class="flex-space-between flexed-align-center" style="padding: 0 13px">
      <div>
        <h3>
          <ion-icon name="flash-outline" style="font-size: 1.8rem; margin-right: 5px"></ion-icon>Automate Actions &
          Create Custom Workflows
        </h3>
        <p class="subtitle">
          Trigger custom functions in response to conversation updates and events.
          <a href="https://docs.unthread.io/conversations/automations" target="_blank">
            Learn more
            <ion-icon name="chevron-forward-outline"></ion-icon>
          </a>
        </p>
      </div>
      <ion-button (click)="createAutomation()" [hidden]="!automationsEnabled || !canEditAutomations">
        <ion-icon name="add-outline"></ion-icon>
        <span class="text-with-left-icon hidden-sm"> Create automation </span>
      </ion-button>
    </div>
    <div style="padding: 5px 0 13px 0px" class="hidden-sm" [hidden]="!automationsEnabled || !canEditAutomations">
      <span class="tab-nav-link" [ngClass]="{ 'tab-nav-active': tab === 'list' }" (click)="selectTab('list')">
        Automations
      </span>
      <span
        class="tab-nav-link"
        [ngClass]="{ 'tab-nav-active': tab === 'configuration' }"
        (click)="selectTab('configuration')"
      >
        Configuration
      </span>
      <span class="tab-nav-link" [ngClass]="{ 'tab-nav-active': tab === 'usage' }" (click)="selectTab('usage')">
        Usage
      </span>
    </div>
  </div>
</ion-header>

<ion-content>
  <div class="constrained-content">
    <div *ngIf="tab === 'list'" style="padding: 15px 0">
      <!-- Search and Filter Controls -->
        <div class="flexed-align-center" *ngIf="automationsEnabled" style="margin-bottom: 10px;">
          <ion-searchbar
            mode="ios"
            placeholder="Search automations..."
            showCancelButton="never"
            showClearButton="never"
            class="rounded-search-bar"
            [(ngModel)]="searchText"
            (ionChange)="handleFilters()"
            style="height: 32px; margin-left: 10px"
          ></ion-searchbar>
          <ion-select
            [(ngModel)]="selectedTriggerType"
            (ionChange)="handleFilters()"
            placeholder="Trigger Type"
            class="select-outline filter-select"
            interface="popover"
          >
            <ion-select-option value="all">Trigger type</ion-select-option>
            <ion-select-option 
              *ngFor="let type of getAvailableTriggerTypes()" 
              [value]="type"
            >
              {{ getTriggerTypeLabel(type) }}
            </ion-select-option>
          </ion-select>
          <ion-select
            [(ngModel)]="selectedStatus"
            (ionChange)="handleFilters()"
            placeholder="Status"
            class="select-outline filter-select"
            interface="popover"
          >
            <ion-select-option value="all">Status</ion-select-option>
            <ion-select-option value="active">Status: Active</ion-select-option>
            <ion-select-option value="paused">Status: Paused</ion-select-option>
          </ion-select>
          <ion-button
            *ngIf="searchText || selectedTriggerType !== 'all' || selectedStatus !== 'all'"
            fill="clear"
            color="medium"
            (click)="clearFilters()"
            class="btn-small"
          >
            <ion-icon name="close-circle-outline" class="padded-icon"></ion-icon>
            Clear filters
          </ion-button>
        </div>

      <ion-card *ngIf="!automationsEnabled">
        <div class="empty-state-container" style="padding: 80px 40px">
          <span class="placeholder-emoji">
            <ion-icon name="flash-outline"></ion-icon>
          </span>
          <h1 class="placeholder-title">Set Up Automations and Triggers</h1>
          <p class="placeholder-subtitle">
            You can integrate with any system and run your own code in response to Unthread events.
          </p>
          <div>
            <ion-button (click)="requestAccess()" *ngIf="!automationsEnabled" [disabled]="isLoading">
              Request access
            </ion-button>
          </div>
        </div>
      </ion-card>
      <ion-card class="outline-card" *ngIf="automationsEnabled">
        <ngx-datatable
          class="material datatable-row-borders"
          columnMode="force"
          [limit]="100"
          [headerHeight]="46"
          [footerHeight]="50"
          [rowHeight]="52"
          [scrollbarH]="true"
          [rows]="filteredAutomations"
          [reorderable]="true"
          [loadingIndicator]="isLoading"
        >
          <ngx-datatable-column name="Name" prop="name" [minWidth]="380">
            <ng-template let-value="value" let-row="row" ngx-datatable-cell-template>
              <a class="row-title row-link" (click)="editAutomation(row.id)">{{ value }}</a>
            </ng-template>
          </ngx-datatable-column>

          <ngx-datatable-column name="Workflow" prop="trigger" [minWidth]="320">
            <ng-template let-value="value" let-row="row" ngx-datatable-cell-template>
              <ion-badge color="light" *ngIf="value.type === 'webhook'" class="row-badge">
                <ion-icon name="custom-webhooks"></ion-icon> webhook
              </ion-badge>
              <ion-badge color="light" *ngIf="value.type === 'manual'" class="row-badge">
                <ion-icon name="build" color="secondary"></ion-icon> manual
              </ion-badge>
              <ion-badge color="light" *ngIf="value.type === 'event'" class="row-badge">
                <ion-icon name="flash" color="tertiary"></ion-icon>
                {{ value.data.objectType }} {{ formatTriggerEvent(value.data.event) }}
              </ion-badge>
              <ion-badge color="light" *ngIf="value.type === 'scheduled'" class="row-badge">
                <ion-icon name="time" color="primary"></ion-icon>
                scheduled
              </ion-badge>
              <ion-badge color="light" *ngIf="value.type === 'slackReaction'" class="row-badge">
                <ion-icon name="flash" color="tertiary"></ion-icon>
                reaction added {{ value.data.emojis }}
              </ion-badge>
              <ion-badge color="light" class="row-badge" *ngFor="let step of row.steps" [ngxTippy]="getStepLabel(step)">
                <ion-icon [name]="getStepIcon(step)"></ion-icon>
              </ion-badge>
            </ng-template>
          </ngx-datatable-column>

          <ngx-datatable-column name="Status" prop="status" [minWidth]="120" [maxWidth]="120">
            <ng-template let-value="value" let-row="row" ngx-datatable-cell-template>
              <ion-badge color="success" *ngIf="value === 'active'" class="row-badge">Active </ion-badge>
              <ion-badge color="warning" *ngIf="value !== 'active'" class="row-badge">Paused </ion-badge>
            </ng-template>
          </ngx-datatable-column>

          <ngx-datatable-column name="Description" prop="description" [minWidth]="300">
            <ng-template let-value="value" let-row="row" ngx-datatable-cell-template>
              {{ value }}
            </ng-template>
          </ngx-datatable-column>

          <ngx-datatable-column name="Updated" prop="updatedAt" [minWidth]="120" [maxWidth]="120">
            <ng-template let-value="value" let-row="row" ngx-datatable-cell-template>
              {{ value | date: 'M/d/yyyy' }}
            </ng-template>
          </ngx-datatable-column>
        </ngx-datatable>
      </ion-card>
    </div>
    <ion-grid *ngIf="tab === 'configuration'">
      <ion-row [hidden]="!automationsEnabled">
        <ion-col size-md="3" size="12" class="settings-title-section">
          <h1><ion-icon name="code-outline"></ion-icon>Custom Functions</h1>
          <p class="subtitle">
            Custom functions are written in TypeScript and allow you to write your own integrations for use in
            automations.
          </p>
        </ion-col>
        <ion-col size-md="9" size="12">
          <div class="button-container">
            <ion-button (click)="editFunctions()" color="dark" fill="clear" class="button-dark-border">
              <ion-icon name="code-outline"></ion-icon>
              <span class="text-with-left-icon hidden-sm"> Edit functions </span>
            </ion-button>
          </div>
          <ion-card class="outline-card">
            <ngx-datatable
              class="material"
              columnMode="force"
              [limit]="100"
              [headerHeight]="50"
              [footerHeight]="50"
              [rowHeight]="50"
              [scrollbarH]="true"
              [rows]="functions"
              [reorderable]="true"
            >
              <ngx-datatable-column name="Name" prop="name" [minWidth]="200">
                <ng-template let-value="value" let-row="row" ngx-datatable-cell-template>
                  {{ value }}
                </ng-template>
              </ngx-datatable-column>
            </ngx-datatable>
          </ion-card>
        </ion-col>
      </ion-row>

      <ion-row [hidden]="!automationsEnabled">
        <ion-col size-md="3" size="12" class="settings-title-section">
          <h1><ion-icon name="shield-checkmark-outline"></ion-icon>Automation Secrets</h1>
          <p class="subtitle">Environment variables to be available for use in automations.</p>
        </ion-col>
        <ion-col size-md="9" size="12">
          <div class="button-container">
            <ion-button (click)="openAddSecretModal()" color="dark" fill="clear" class="button-dark-border">
              <ion-icon name="add-outline"></ion-icon>
              <span class="text-with-left-icon hidden-sm"> Add secret </span>
            </ion-button>
          </div>
          <ion-card class="outline-card">
            <ngx-datatable
              class="material"
              columnMode="force"
              [limit]="100"
              [headerHeight]="50"
              [footerHeight]="50"
              [rowHeight]="50"
              [scrollbarH]="true"
              [rows]="automationSecrets"
              [reorderable]="true"
            >
              <ngx-datatable-column name="Name" prop="name" [minWidth]="300">
                <ng-template let-value="value" let-row="row" ngx-datatable-cell-template>
                  {{ value }}
                </ng-template>
              </ngx-datatable-column>

              <ngx-datatable-column name="" prop="name" [minWidth]="100" [maxWidth]="100">
                <ng-template let-value="value" let-row="row" ngx-datatable-cell-template>
                  <a (click)="deleteAutomationSecret(row)">Delete</a>
                </ng-template>
              </ngx-datatable-column>
            </ngx-datatable>
          </ion-card>
        </ion-col>
      </ion-row>

      <ion-row [hidden]="!automationsEnabled">
        <ion-col size-md="3" size="12" class="settings-title-section">
          <h1><ion-icon name="server-outline"></ion-icon>Automation Key-Value Stores</h1>
          <p class="subtitle">Key-Value stores to be available for use in automations.</p>
        </ion-col>
        <ion-col size-md="9" size="12">
          <div class="button-container">
            <ion-button (click)="openAddStoreModal()" color="dark" fill="clear" class="button-dark-border">
              <ion-icon name="add-outline"></ion-icon>
              <span class="text-with-left-icon hidden-sm"> Add store </span>
            </ion-button>
          </div>
          <ion-card class="outline-card">
            <ngx-datatable
              class="material"
              columnMode="force"
              [limit]="100"
              [headerHeight]="50"
              [footerHeight]="50"
              [rowHeight]="50"
              [scrollbarH]="true"
              [rows]="automationStores"
              [reorderable]="true"
            >
              <ngx-datatable-column name="Name" prop="name" [minWidth]="300">
                <ng-template let-value="value" let-row="row" ngx-datatable-cell-template>
                  {{ value }}
                </ng-template>
              </ngx-datatable-column>

              <ngx-datatable-column name="Description" prop="description" [minWidth]="300">
                <ng-template let-value="value" let-row="row" ngx-datatable-cell-template>
                  {{ value }}
                </ng-template>
              </ngx-datatable-column>

              <ngx-datatable-column name="" prop="name" [minWidth]="100" [maxWidth]="100">
                <ng-template let-value="value" let-row="row" ngx-datatable-cell-template>
                  <a (click)="deleteAutomationStore(row.name)">Delete</a>
                </ng-template>
              </ngx-datatable-column>
            </ngx-datatable>
          </ion-card>
        </ion-col>
      </ion-row>
    </ion-grid>
    <ion-grid *ngIf="tab === 'usage'">
      <ion-row>
        <ion-col size="6">
          <h3 style="margin-left: 10px">Last Month</h3>
          <ion-card class="outline-card report-card">
            <span class="report-stat">
              <span class="stat-large">{{ automationRunCountLastMonth ?? '...' }}</span>
            </span>
            <div class="report-title">Automation runs last month</div>
          </ion-card>
        </ion-col>
        <ion-col size="6">
          <h3 style="margin-left: 10px">This Month</h3>
          <ion-card class="outline-card report-card">
            <span class="report-stat">
              <span class="stat-large">{{ automationRunCountThisMonth ?? '...' }}</span>
            </span>
            <div class="report-title">Automation runs this month</div>
          </ion-card>
        </ion-col>
      </ion-row>
    </ion-grid>
  </div>
</ion-content>

<ion-modal
  [isOpen]="showAutomationAddSecretModal"
  (willDismiss)="onDismissAutomationAddSecretModal()"
  class="automation-add-secret-modal"
>
  <ng-template>
    <ion-header>
      <ion-toolbar>
        <ion-title>Add Automation Secret</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="onDismissAutomationAddSecretModal()"><ion-icon name="close"></ion-icon></ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content class="ion-padding">
      <div style="padding: 20px">
        <ion-label>
          <strong>Name</strong>
        </ion-label>
        <ion-input placeholder="e.g. API_TOKEN" [(ngModel)]="automationSecret.name" class="input-field"> </ion-input>
        <ion-label>
          <strong>Value</strong>
        </ion-label>
        <ion-textarea [(ngModel)]="automationSecret.value" class="input-field code" rows="14"> </ion-textarea>
      </div>
    </ion-content>
    <ion-footer class="footer flex-space-between">
      <ion-button (click)="addAutomationSecret()" [disabled]="isLoading">
        Save
        <ion-spinner name="crescent" [hidden]="!isLoading" style="margin-left: 10px"></ion-spinner>
      </ion-button>
    </ion-footer>
  </ng-template>
</ion-modal>

<ion-modal
  [isOpen]="showAutomationAddStoreModal"
  (willDismiss)="onDismissAutomationAddStoreModal()"
  class="automation-add-secret-modal"
>
  <ng-template>
    <ion-header>
      <ion-toolbar>
        <ion-title>Add Automation Key-Value Store</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="onDismissAutomationAddStoreModal()"><ion-icon name="close"></ion-icon></ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content class="ion-padding">
      <div style="padding: 20px">
        <ion-label>
          <strong>Name</strong>
        </ion-label>
        <ion-input placeholder="e.g. my_key_value_store" [(ngModel)]="automationStore.name" class="input-field">
        </ion-input>
        <ion-label>
          <strong>Description</strong>
        </ion-label>
        <ion-textarea [(ngModel)]="automationStore.description" class="input-field" rows="5"> </ion-textarea>
      </div>
    </ion-content>
    <ion-footer class="footer flex-space-between">
      <ion-button (click)="addAutomationStore()" [disabled]="isLoading">
        Save
        <ion-spinner name="crescent" [hidden]="!isLoading" style="margin-left: 10px"></ion-spinner>
      </ion-button>
    </ion-footer>
  </ng-template>
</ion-modal>
